import { useMutation } from "@tanstack/react-query";
import {
  activateSiteMutation,
  configureSiteMutation,
  deactivateSiteMutation,
  deleteSiteMutation,
  patchSiteMutation,
} from "../../services/client/@tanstack/react-query.gen";
import {
  UpdateSiteConfigurationDto,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import OptimizationConfigurationForm from "../NewSite/Components/OptimizationConfigurationForm/OptimizationConfigurationForm";
import Form, { useForm } from "antd/es/form/Form";
import { Alert, Button, Segmented, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../util-components/Notifications/notifications";
import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import { isReady } from "../SiteDetails/ValidationTab/utils";
import Paragraph from "antd/es/typography/Paragraph";
import DsoConnectionForm from "./DsoConnectionForm";
import ChargingRevenueForm from "./ChargingRevenueForm";
import EnergyContractForm from "./EnergyContractForm";
import IngressValidationForm from "./IngressValidationForm";
import SiteDetailsForm from "./SiteDetailsForm";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

export type SettingsProps = {
  site: Site;
  refetchSite: () => void;
};

type SiteStatus = NonNullable<Site["status"]>;

const statusChangeMessage: Record<SiteStatus, string> = {
  inactive: "Site deactivated: forecasting and optimization stopped.",
  shadow: "Shadow mode enabled: schedules will not be sent to the controller.",
  active: "Site activated: schedules will be sent to the controller.",
};

export const Settings: React.FC<SettingsProps> = ({ site, refetchSite }) => {
  const [isDirty, setIsDirty] = useState(
    site.configurations?.optimization_configuration == null,
  );
  const notify = useNotify();
  const isViewer = useIsViewer();

  const [form] = useForm();
  const navigate = useNavigate();

  const configureSite = useMutation({
    ...configureSiteMutation(),
  });

  const deleteSite = useMutation({
    ...deleteSiteMutation(),
  });

  const activateSite = useMutation({
    ...activateSiteMutation(),
  });

  const deactivateSite = useMutation(deactivateSiteMutation());

  const patchSite = useMutation(patchSiteMutation());

  const notifyError = () => {
    notify({
      type: "error",
      message: "Something went wrong, please try again!",
    });
  };

  const currentStatus: SiteStatus = site.status ?? "inactive";
  const statusChangePending =
    activateSite.isPending || deactivateSite.isPending || patchSite.isPending;
  const activationBlockedByValidation =
    currentStatus !== "active" && !isReady(site.validation_status?.status);

  const onChangeStatus = (nextStatus: SiteStatus) => {
    if (!site || !site.site_id) {
      return navigate("/404");
    }
    if (nextStatus === currentStatus) {
      return;
    }

    const callbacks = {
      onError: notifyError,
      onSuccess: () => {
        refetchSite();
        notify({ type: "success", message: statusChangeMessage[nextStatus] });
      },
    };

    if (nextStatus === "active") {
      activateSite.mutate({ path: { site_id: site.site_id } }, callbacks);
    } else if (nextStatus === "inactive") {
      deactivateSite.mutate({ path: { site_id: site.site_id } }, callbacks);
    } else {
      patchSite.mutate(
        { path: { site_id: site.site_id }, body: { status: "shadow" } },
        callbacks,
      );
    }
  };

  const onDelete = () => {
    if (!site || !site.site_id) {
      return navigate("/404");
    }

    deleteSite.mutate(
      { path: { site_id: site.site_id } },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong, please try again!",
          });
        },
        onSettled: () => {
          notify({
            type: "success",
            message: "Site deleted succesfully!",
          });
          navigate("/sites");
        },
      },
    );
  };

  const submit = (
    configurations: Omit<UpdateSiteConfigurationDto, "site_configuration">,
  ) => {
    if (!site || !site.site_id) {
      return navigate("/404");
    }

    configureSite.mutate(
      {
        path: { site_id: site?.site_id },
        body: {
          optimization_configuration: configurations.optimization_configuration,
        },
      },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong, please try again!",
          });
        },
        onSettled: () => {
          refetchSite();
          notify({
            type: "success",
            message: "Site settings updated successfully!",
          });
        },
      },
    );
    setIsDirty(false);
  };
  return (
    <div className="w-full  xl:w-2/3">
      <SiteDetailsForm site={site} refetchSite={refetchSite} />
      <Form
        initialValues={site.configurations ?? undefined}
        form={form}
        onFinish={submit}
        onFieldsChange={() => setIsDirty(true)}
        disabled={isViewer}
      >
        <OptimizationConfigurationForm
          form={form}
          markFormDirty={() => setIsDirty(true)}
        />
        <DisableForViewer>
          <Button type="primary" htmlType="submit" disabled={!isDirty}>
            {site.configurations?.optimization_configuration == null
              ? "Save"
              : "Update"}
          </Button>
        </DisableForViewer>
        {configureSite.isError && (
          <Alert
            message="Error"
            description={
              "Something went wrong while updating the optimization settings."
            }
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
      <DsoConnectionForm site={site} refetchSite={refetchSite} />
      <EnergyContractForm site={site} refetchSite={refetchSite} />
      <ChargingRevenueForm site={site} refetchSite={refetchSite} />
      <div className="mt-4">
        <div className="flex flex-col items-between justify-center gap-4 w-full md:w-6/12 lg:w-8/12">
          <Title level={3}>MQTT Configuration</Title>

          <Paragraph>
            Connect to the MQTT cluster and publish the relevant data on the
            following topics:
          </Paragraph>

          <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(300px,1fr)]">
            <Text strong>MQTT Cluster</Text>
            <Text code className="text-red-500">
              {site.connections?.mqtt.cluster || "Please contact support!"}
            </Text>

            <Text>Energy measurements topic</Text>
            <Text code className="text-red-500">
              {site.connections?.mqtt.energy_measurement_topic ||
                "Please contact support!"}
            </Text>

            <Text>Transactions topic</Text>
            <Text code className="text-red-500">
              {site.connections?.mqtt.transaction_topic ||
                "Please contact support!"}
            </Text>

            <Text>Advice topic</Text>
            <Text code className="text-red-500">
              {site.connections?.mqtt.advice_topic || "Please contact support!"}
            </Text>

            <Text>Notifications topic</Text>
            <Text code className="text-red-500">
              {site.connections?.mqtt.notification_topic ||
                "Please contact support!"}
            </Text>
          </div>
        </div>
        <div className="mt-6">
          <Text strong>Helpful documentation</Text>
          <ul className="list-disc list-inside text-blue-600">
            <li>
              <a
                href="https://api-docs.pleevi.ai/tutorial/getting-started/sending-data"
                target="_blank"
                rel="noreferrer"
              >
                How to send energy measurements & transactions
              </a>
            </li>
            <li>
              <a
                href="https://api-docs.pleevi.ai/tutorial/getting-started/listen-to-advices"
                target="_blank"
                rel="noreferrer"
              >
                How to listen for advices
              </a>
            </li>
            <li>
              <a
                href="https://api-docs.pleevi.ai/tutorial/notifications"
                target="_blank"
                rel="noreferrer"
              >
                Notifications explained
              </a>
            </li>
          </ul>
        </div>
      </div>
      <IngressValidationForm site={site} refetchSite={refetchSite} />
      <div className="mt-4">
        <Title level={3}>Site status</Title>
        <div className="flex justify-between items-center gap-4 w-full md:w-6/12 lg:w-8/12">
          <Text type="secondary">
            Inactive sites run nothing. Shadow sites run forecasting and
            optimization without sending schedules to the controller. Active
            sites run everything.
          </Text>

          <Tooltip
            title={
              activationBlockedByValidation
                ? "Site not ready for optimization."
                : ""
            }
          >
            <DisableForViewer>
              <Segmented
                value={currentStatus}
                disabled={statusChangePending}
                onChange={(value) => onChangeStatus(value as SiteStatus)}
                options={[
                  { label: "Inactive", value: "inactive" },
                  { label: "Shadow", value: "shadow" },
                  {
                    label: "Active",
                    value: "active",
                    disabled: activationBlockedByValidation,
                  },
                ]}
              />
            </DisableForViewer>
          </Tooltip>
        </div>
      </div>
      <div className="mt-4">
        <Title level={3}>Danger zone</Title>
        <div className="flex justify-between items-center gap-4 w-full md:w-6/12 lg:w-8/12">
          <Text type="secondary">
            Site removal. This action cannot be undone.
          </Text>

          <DisableForViewer>
            <Button
              type="default"
              className="w-36"
              onClick={onDelete}
              loading={deleteSite.isPending}
              disabled={deleteSite.isPending}
            >
              <Text type="danger">Remove site</Text>
            </Button>
          </DisableForViewer>
        </div>
      </div>
    </div>
  );
};
